import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  NOTIFICATION_TYPE_CONFIG,
  NotificationService,
} from 'src/app/services/notification.service';

interface SettingRow {
  type: string;
  label: string;
  description: string;
  icon: string;
  bgClass: string;
  enabled: boolean;
}

@Component({
  selector: 'app-notification-settings',
  templateUrl: './notification-settings.component.html',
  styleUrls: ['./notification-settings.component.sass'],
  providers: [MessageService],
})
export class NotificationSettingsComponent implements OnInit, OnDestroy {
  settings: SettingRow[] = [];
  loading = true;
  saving = false;

  private toggleSubject = new Subject<void>();
  private sub: Subscription;

  constructor(
    private notificationService: NotificationService,
    private messageService: MessageService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadSettings();
    this.sub = this.toggleSubject
      .pipe(debounceTime(400))
      .subscribe(() => this.save());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async loadSettings(): Promise<void> {
    try {
      const data = await this.notificationService.getSettings();
      const enabledMap = new Map(data.map((s) => [s.type, s.enabled]));
      this.settings = Object.entries(NOTIFICATION_TYPE_CONFIG).map(([type, config]) => ({
        type,
        label: config.label,
        description: config.description ?? '',
        icon: config.icon,
        bgClass: config.bgClass,
        enabled: enabledMap.get(type) ?? true,
      }));
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Load Error',
        detail: 'Failed to load notification preferences.',
        life: 3000,
      });
    } finally {
      this.loading = false;
    }
  }

  onToggle(): void {
    this.toggleSubject.next();
  }

  async save(): Promise<void> {
    this.saving = true;
    try {
      await this.notificationService.updateSettings(
        this.settings.map((s) => ({ type: s.type, enabled: s.enabled })),
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'Notification preferences updated.',
        life: 2500,
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Save Error',
        detail: 'Failed to save notification preferences.',
        life: 3000,
      });
    } finally {
      this.saving = false;
    }
  }
}
